import { useCallback, type Ref, type RefCallback } from "react";

/**
 * The scroll fade (`contracts/design-system.md` § Scroll fade, 2026-10-21): a scroll region fades
 * at an edge its content continues past, so a line that runs on past a card's rounded edge or under a composer
 * reads as cut by the region rather than by the card. `ScrollFade.css` draws it as a mask on the scroller, from
 * what this module writes there:
 *
 * - `data-scroll-edge`, where the content stands against the scroller: `none` while nothing overflows, `at-start`
 *   (more below), `scrolled` (more on both sides) or `at-end` (more above);
 * - `--scroll-fade-inset-start` and `--scroll-fade-inset-end`, the depth a sticky head or foot covers at the
 *   scroller's top or bottom, measured from the element the host names in `under` or `over`, so the fade starts
 *   under a table's header row or a list's group header rather than fading the head itself.
 *
 * The state is read from the scroll position on the next animation frame after a scroll, and again whenever the
 * scroller or one of its children changes size (content arriving, a panel resized), so a region that grows into
 * overflow fades without a scroll. It is written to the element directly and never through React state, so a
 * scroll re-renders nothing.
 */
export type ScrollEdge = "none" | "at-start" | "scrolled" | "at-end";

/** Where a scroller stands, from its offset, its visible height and its content's height; a pixel of slack absorbs a fractional offset. */
export function edgeOf(scroll: number, client: number, total: number): ScrollEdge {
  if (total <= client + 1) return "none";
  if (scroll < 1) return "at-start";
  if (scroll + client >= total - 1) return "at-end";
  return "scrolled";
}

export interface ScrollFadeOptions {
  /** A selector for a head that sticks at the scroller's top (a table's header row, a list's group header); the top fade starts under it. */
  readonly under?: string;
  /** A selector for a foot that sticks at the scroller's bottom (a table's totals row, the bulk bar); the bottom fade ends above it. */
  readonly over?: string;
}

const INSET_START = "--scroll-fade-inset-start";
const INSET_END = "--scroll-fade-inset-end";

/** How far into the scroller's visible box, from its top or its bottom, the element `selector` names reaches; 0 when none is there. */
function coveredDepth(node: HTMLElement, selector: string, side: "start" | "end"): number {
  const covering = node.querySelector(selector);
  if (covering === null) return 0;
  const box = node.getBoundingClientRect();
  const top = box.top + node.clientTop;
  const rect = covering.getBoundingClientRect();
  const depth = side === "start" ? rect.bottom - top : top + node.clientHeight - rect.top;
  return Math.min(Math.max(depth, 0), node.clientHeight);
}

/** Starts the fade on `node` and returns what stops it, which also clears what it wrote. */
export function attachScrollFade(node: HTMLElement, { under, over }: ScrollFadeOptions = {}): () => void {
  let frame = 0;
  const update = () => {
    frame = 0;
    node.dataset.scrollEdge = edgeOf(node.scrollTop, node.clientHeight, node.scrollHeight);
    if (under !== undefined) node.style.setProperty(INSET_START, `${String(coveredDepth(node, under, "start"))}px`);
    if (over !== undefined) node.style.setProperty(INSET_END, `${String(coveredDepth(node, over, "end"))}px`);
  };
  const schedule = () => {
    if (frame === 0) frame = requestAnimationFrame(update);
  };
  update();
  node.addEventListener("scroll", schedule, { passive: true });
  // The scroller's own size and each child's: a child that grows or shrinks is what changes the content's height.
  const sizes = new ResizeObserver(schedule);
  sizes.observe(node);
  for (const child of node.children) sizes.observe(child);
  const children = new MutationObserver((records) => {
    for (const record of records) {
      for (const added of record.addedNodes) if (added instanceof Element) sizes.observe(added);
    }
    schedule();
  });
  children.observe(node, { childList: true });
  return () => {
    if (frame !== 0) cancelAnimationFrame(frame);
    node.removeEventListener("scroll", schedule);
    sizes.disconnect();
    children.disconnect();
    delete node.dataset.scrollEdge;
    node.style.removeProperty(INSET_START);
    node.style.removeProperty(INSET_END);
  };
}

/** Hands `node` to a host's own ref, an object or a callback, and returns what hands it back on detach. */
function share<T>(ref: Ref<T> | undefined, node: T): () => void {
  if (typeof ref === "function") {
    const cleanup = ref(node);
    return typeof cleanup === "function" ? cleanup : () => { ref(null); };
  }
  if (ref === null || ref === undefined) return () => undefined;
  ref.current = node;
  return () => { ref.current = null; };
}

/**
 * The scroll fade as a ref for the scroller: `<div ref={useScrollFade()} className="…overflow-y-auto">`. A host that
 * keeps its own ref to the same element passes it as `ref`, and it holds the element while it is mounted.
 */
export function useScrollFade<T extends HTMLElement>({ under, over, ref }: ScrollFadeOptions & { readonly ref?: Ref<T> } = {}): RefCallback<T> {
  return useCallback(
    (node: T | null) => {
      if (node === null) return undefined;
      const release = share(ref, node);
      const detach = attachScrollFade(node, { ...(under === undefined ? {} : { under }), ...(over === undefined ? {} : { over }) });
      return () => {
        detach();
        release();
      };
    },
    [under, over, ref],
  );
}
